import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ModalType } from './modalStore';

export enum TOUR_TYPES {
  ONBOARDING_FLOW = 'ONBOARDING_FLOW', // welcome onbooarding flag
  ONBOARDING = 'ONBOARDING', // product tour flag
  NEWS_DETAILS = 'NEWS_DETAILS',
  RADIO_HOME = 'RADIO_HOME',
  AUDIO_PLAYER = 'AUDIO_PLAYER',
}
const IMMERSIVE_HINT_MAX_SHOWS = 4;
const MAX_ONBOARDING_SHOWS = 1;
type TourState = {
  tours: Record<TOUR_TYPES, boolean>;
  activeTour: TOUR_TYPES;
  swipeHintCount: number;
  onboardingShowCount: number;

  hasSeen: (tour: TOUR_TYPES) => boolean;
  setSeen: (tour: TOUR_TYPES) => void;
  resetTour: (tour: TOUR_TYPES) => void;
  resetAll: () => void;

  getActiveTour: () => TOUR_TYPES;
  setActiveTour: (tour: TOUR_TYPES) => void;
  incrementSwipeHint: () => void;
  shouldShowImmersiveSwipeHint: () => boolean;


  shouldShowOnboarding: () => boolean;
  skipOnboarding: () => void;
  setOnboardingCompleted: () => void;
};

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      tours: {
        [TOUR_TYPES.ONBOARDING_FLOW]: false,
        [TOUR_TYPES.ONBOARDING]: false,
        [TOUR_TYPES.NEWS_DETAILS]: false,
        [TOUR_TYPES.RADIO_HOME]: false,
        [TOUR_TYPES.AUDIO_PLAYER]: false,
      },
      swipeHintCount: 0,
      onboardingShowCount: 0,
      hasSavedAnyCollection: false,
      followCollectionHintShownCount: 0,
      activeTour: TOUR_TYPES.ONBOARDING,

      hasSeen: (tour) => get().tours[tour] === true,

      setSeen: (tour) =>
        set((state) => ({
          tours: {
            ...state.tours,
            [tour]: true,
          },
        })),

      resetTour: (tour) =>
        set((state) => ({
          tours: {
            ...state.tours,
            [tour]: false,
          },
        })),

      incrementSwipeHint: () =>
        set((state) => ({ swipeHintCount: state.swipeHintCount + 1 })),

      shouldShowImmersiveSwipeHint: () =>
        get().swipeHintCount < IMMERSIVE_HINT_MAX_SHOWS,


      shouldShowOnboarding: () => get().onboardingShowCount < MAX_ONBOARDING_SHOWS,

      skipOnboarding: () =>
        set((state) => ({
          onboardingShowCount: Math.min(
            state.onboardingShowCount + 1,
            MAX_ONBOARDING_SHOWS
          ),
        })),

      setOnboardingCompleted: () =>
        set({ onboardingShowCount: MAX_ONBOARDING_SHOWS }),

      resetAll: () =>
        set({
          swipeHintCount: 0,
          onboardingShowCount: 0,
          tours: {
            [TOUR_TYPES.ONBOARDING_FLOW]: false,
            [TOUR_TYPES.ONBOARDING]: false,
            [TOUR_TYPES.NEWS_DETAILS]: false,
            [TOUR_TYPES.RADIO_HOME]: false,
            [TOUR_TYPES.AUDIO_PLAYER]: false,
          },
        }),

      getActiveTour: () => get().activeTour,
      setActiveTour: (tour) => set({ activeTour: tour }),
    }),
    {
      name: 'tour-store',
      storage: createJSONStorage(() => AsyncStorage),
      version: 3,
      migrate: (persistedState: any, version) => {
        if (!persistedState) return persistedState;
        if (version === 2) {
          return {
            ...persistedState,
            swipeHintCount: 0,
          };
        }
        if (version === 1) {
          const prev = persistedState as {
            tours?: Record<TOUR_TYPES, boolean>;
          };
          const prevTours = prev.tours ?? {} as Record<TOUR_TYPES, boolean>;
          const onboardingWasCompleted = prevTours[TOUR_TYPES.ONBOARDING] === true;

          return {
            ...persistedState,
            swipeHintCount: 0,
            onboardingShowCount: onboardingWasCompleted
              ? MAX_ONBOARDING_SHOWS
              : 0,
            tours: {
              ...prevTours,
              [TOUR_TYPES.ONBOARDING]: false,
            },
          };
        }
        return persistedState;
      },
    }
  )
);

export const getSteps = (t: any, tour: TOUR_TYPES, showOnWeb?: boolean) => {
  const TOUR_GROUPS = {
    ONBOARDING: [
      {
        disableBeacon: true,
        target: '#tabSwitch',
        content: t("screens.tour.tabSwitch"),
      },
      {
        target: `#${ModalType.LANGUAGES}`,
        content: t("screens.tour.langSwitch"),
      },
      // {
      //   target: `#${ModalType.MENU}`,
      //   content: t("screens.tour.profile"),
      //   placement: showOnWeb ? 'bottom-end' : 'top-end'
      // }, 
      {
        target: `#${ModalType.AI_ASSISTANT}`,
        content: t("screens.tour.thinkoAssist"),
         placement: showOnWeb ? 'top-end' : 'top'
      },
      {
        target: '#manageCollections',
        content: t("screens.tour.manageCollections"),
        placement: 'bottom-end'
      },
      // {
      //   target: '#collections',
      //   content: t("screens.tour.collections"),
      // },
      // {
      //   target: '#followCollectionIcon',
      //   content: t("screens.tour.tapToFollowCollection"),
      // },
      // {
      //   target: '#templateMenu',
      //   content: t("screens.tour.templateMenu"),
      //   placement: 'bottom-end'
      // },
      {
        target: '#templateShare',
        content: t("screens.tour.templateShare"),
        placement: 'bottom-end'
      },
    ],

    NEWS_DETAILS: [
      {
        disableBeacon: true,
        target: '#GIST',
        content: t("screens.tour.gist"),
      },
      {
        target: '#STORY_SO_FAR-1',
        content: t("screens.tour.storySoFar"),
      },
      {
        target: '#RIPPLE_EFFECT-1',
        content: t("screens.tour.rippleEffect"),
      },
      {
        target: '#NEXT_DROP-2',
        content: t("screens.tour.nextDrop"),
        placement: 'bottom-end'
      }
    ],
    RADIO_HOME: [
      {
        disableBeacon: true,
        target: '#audioToggle',
        content: t("screens.tour.audioToggle"),
        placement: 'top-end'
      }
    ],
    AUDIO_PLAYER: [
      {
        disableBeacon: true,
        target: '#speedToggle',
        content: t("screens.tour.speedToggle"),
        placement: 'bottom-end'
      },
      {
        target: '#audioPlaylist',
        content: t("screens.tour.audioPlaylist"),
        placement: 'top-end'
      }
    ]
  };
  return TOUR_GROUPS[tour]
};
